import { LoadingContent } from '../components/feedback';
import { McpPolicySection } from './McpPolicySection';
import { HomepageSection } from './HomepageSection';
import { AppearanceSection } from './AppearanceSection';
import { AccountSecurity } from './AccountSecurity';
import { SftpSection } from './SftpSection';
import { NetworkBansSection } from './NetworkBansSection';
import { SharedDatabaseSection } from './SharedDatabaseSection';
import { AboutPanel } from './AboutPanel';
import { DiscordLinksSection } from './DiscordLinksSection';
import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { api3 } from '../api/client-p3';
import { CopyBox } from '../components/CopyBox';
import type { TokenScope } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Dialog, Empty } from '../components/ui';
import { formatRelative, roleLabel } from '../format';
import { usePoll } from '../hooks/usePoll';
import { useSearchParams } from 'react-router';
import { ServerSections } from '../components/ServerNavigation';
import { PageTransition } from '../components/motion';
import { resolveSettingsTab, visibleSettingsTabs } from './settings-navigation';

function Tokens() {
  const { me, notify } = useApp();
  const tokens = usePoll(() => api.tokens(), 60000);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [operate, setOperate] = useState(true);
  const [secret, setSecret] = useState<string>();

  const create = async (event: FormEvent) => {
    event.preventDefault();
    const scopes: TokenScope[] = operate ? ['read', 'operate'] : ['read'];
    try {
      const result = await api.createToken(name.trim(), scopes);
      setSecret(result.secret);
      setCreating(false);
      setName('');
      await tokens.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('このトークンを失効させますか？このトークンを使っている AI は接続できなくなります。')) return;
    await api.revokeToken(id);
    await tokens.reload();
  };

  const active = (tokens.data ?? []).filter((t) => !t.revokedAt);
  const mcpUrl = me?.mcpUrl || `${location.origin}/mcp`;

  return (
    <section className="card">
      <div className="card-header">
        <h2 data-search-id="tokens">AI 用アクセストークン（MCP）</h2>
        <button className="btn small" onClick={() => setCreating(true)}>
          作成
        </button>
      </div>
      <div className="card-body stack" style={{ gap: 8 }}>
        <p className="small muted" style={{ margin: 0 }}>
          Claude などの AI がこのパネルを操作するためのトークンです。AI
          は標準設定で通常の変更をすぐ実行できます。削除・復元などは承認待ちになります。
        </p>
        <div className="small stack" style={{ gap: 4 }}>
          <strong>Claude のアプリ（claude.ai・デスクトップ・スマホ）から使う</strong>
          <span className="muted">
            「設定 → コネクタ → カスタムコネクタを追加」で、次の URL だけを貼ります。トークンは不要です。追加すると
            このパネルのログイン画面が開くので、ログインして許可します。
          </span>
          <CopyBox value={mcpUrl} />
        </div>
        <div className="small stack" style={{ gap: 4 }}>
          <strong>Claude Code（ターミナル）から使う</strong>
          <span className="muted">
            下の「作成」でトークンを作り、ターミナルで次のコマンドを実行します（&lt;トークン&gt;
            を置き換える）。トークンを使わず、<code>claude mcp add --transport http mc-panel {mcpUrl}</code>{' '}
            だけを実行してログインで許可することもできます。
          </span>
          <CopyBox
            value={`claude mcp add --transport http mc-panel ${mcpUrl} --header "Authorization: Bearer <トークン>"`}
          />
        </div>
      </div>
      <LoadingContent loading={!tokens.data && tokens.loading} shape="settings">
        {active.length === 0 ? (
          <Empty>トークンはありません。</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>名前</th>
                  <th>権限</th>
                  <th>最終使用</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="small">{t.scopes.includes('operate') ? '閲覧・操作' : '閲覧のみ'}</td>
                    <td className="small muted">{formatRelative(t.lastUsedAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn ghost small danger" onClick={() => void revoke(t.id)}>
                        失効
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadingContent>
      {creating && (
        <Dialog
          title="アクセストークンを作成"
          onClose={() => setCreating(false)}
          actions={
            <>
              <button className="btn" onClick={() => setCreating(false)}>
                キャンセル
              </button>
              <button className="btn primary" form="token-form" disabled={!name.trim()}>
                作成
              </button>
            </>
          }
        >
          <form id="token-form" className="stack" style={{ gap: 12 }} onSubmit={(event) => void create(event)}>
            <div className="field">
              <label htmlFor="token-name">名前（どの AI・どの端末か）</label>
              <input
                id="token-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
              />
            </div>
            <label className="check">
              <input type="checkbox" checked={operate} onChange={(e) => setOperate(e.target.checked)} />
              起動・停止・コンソール操作を許可する
            </label>
          </form>
        </Dialog>
      )}
      {secret && (
        <Dialog
          title="トークンを作成しました"
          onClose={() => setSecret(undefined)}
          actions={
            <button className="btn primary" onClick={() => setSecret(undefined)}>
              保存した
            </button>
          }
        >
          <p>このトークンは今しか表示されません。AI の設定に貼り付けてください。</p>
          <CopyBox value={secret} />
        </Dialog>
      )}
    </section>
  );
}

function OAuthGrants() {
  const { notify } = useApp();
  const grants = usePoll(() => api3.oauthGrants(), 60000);
  const revoke = async (id: string, name: string) => {
    if (!window.confirm(`「${name}」の接続を取り消しますか？`)) return;
    try {
      await api3.revokeOAuthGrant(id);
      await grants.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const list = grants.data ?? [];
  return (
    <section className="card">
      <div className="card-header">
        <h2 data-search-id="oauth">接続を許可した AI アプリ（OAuth）</h2>
      </div>
      <LoadingContent loading={!grants.data && grants.loading} shape="settings">
        {list.length === 0 ? (
          <Empty>許可したアプリはありません。claude.ai のコネクタなどから接続すると、ここに表示されます。</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <tbody>
                {list.map((g) => (
                  <tr key={g.id}>
                    <td>
                      {g.clientName}
                      {g.clientUri && <div className="small faint">{g.clientUri}</div>}
                    </td>
                    <td className="small">{g.scopes.includes('operate') ? '閲覧・操作' : '閲覧のみ'}</td>
                    <td className="small muted">最終使用 {formatRelative(g.lastUsedAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn ghost small danger" onClick={() => void revoke(g.id, g.clientName)}>
                        取り消す
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadingContent>
    </section>
  );
}

export function Settings() {
  const { me } = useApp();
  const [params, setParams] = useSearchParams();
  const role = me?.user?.role;
  const tab = resolveSettingsTab(params, role);
  const tabs = visibleSettingsTabs(role);
  return (
    <div className="stack settings-page">
      <div className="page-header">
        <div>
          <h1 data-search-id="settings">設定</h1>
          <p>
            {me?.user?.name} ・ {me?.user && roleLabel[me.user.role]}
          </p>
        </div>
      </div>
      <ServerSections
        sections={tabs}
        section={tab}
        onChange={(next) => setParams({ tab: next })}
        title={`設定のメニュー（${tabs.find((item) => item.id === tab)?.label}）`}
        heading={false}
      >
        <PageTransition key={tab}>
          {tab === 'account' && (
            <>
              <AccountSecurity />
              <SftpSection />
              <DiscordLinksSection />
            </>
          )}
          {tab === 'appearance' && <AppearanceSection />}
          {tab === 'ai' && (
            <>
              <McpPolicySection />
              <Tokens />
              <OAuthGrants />
            </>
          )}
          {tab === 'network' && (
            <>
              <NetworkBansSection />
              <SharedDatabaseSection />
            </>
          )}
          {tab === 'homepage' && <HomepageSection />}
          {tab === 'about' && <AboutPanel />}
        </PageTransition>
      </ServerSections>
    </div>
  );
}
